import { useEffect, useState } from "react";
import {
  getBrokerStatus,
  saveBrokerConfig,
  type BrokerStatus,
} from "../lib/dispatch";

type Tone = "ok" | "warn" | "err";

function inputClass() {
  return "rounded border border-tv-border bg-tv-bg px-2.5 py-1.5 text-sm text-tv-text outline-none focus:border-tv-blue";
}

export default function BrokerSetup() {
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [server, setServer] = useState("");
  const [setupUrl, setSetupUrl] = useState("");
  const [status, setStatus] = useState<BrokerStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<{ tone: Tone; text: string } | null>(null);

  const refresh = async () => {
    setStatus(await getBrokerStatus());
  };

  useEffect(() => {
    refresh();
  }, []);

  const save = async () => {
    if (!login.trim() || !password || !server.trim()) {
      setMsg({ tone: "warn", text: "Login, Password و Server مطلوبين" });
      return;
    }
    setLoading(true);
    setMsg(null);
    const res = await saveBrokerConfig({
      login: login.trim(),
      password,
      server: server.trim(),
      setup_url: setupUrl.trim() || undefined,
    });
    setLoading(false);

    const s = String(res.body.status ?? "NO_RESPONSE");
    if (s === "SAVED") {
      const gw = res.body.gateway as Record<string, unknown> | undefined;
      setMsg({
        tone: "ok",
        text: `✓ سُجّلت فـ الـ Worker — Gateway: ${gw?.success ?? gw?.error ?? "forwarded"}`,
      });
      refresh();
    } else {
      setMsg({ tone: "err", text: `${s} (${res.status}) — تmaybe ADMIN_TOKEN خاطئ` });
    }
  };

  const toneColor =
    msg?.tone === "ok" ? "text-tv-green" : msg?.tone === "warn" ? "text-tv-yellow" : "text-tv-red";

  return (
    <div className="space-y-4 overflow-y-auto p-4">
      <div className="rounded border border-tv-border bg-tv-panel px-4 py-3">
        <div className="flex items-center gap-2">
          <span
            className={`h-2.5 w-2.5 rounded-full ${
              status?.configured ? "bg-tv-green" : "bg-tv-muted"
            }`}
          />
          <span className="text-sm font-semibold text-tv-text">
            Broker Execution — MetaTrader 5
          </span>
        </div>
        <p className="mt-1 text-xs text-tv-muted">
          {status?.configured ? (
            <>
              <span className="text-tv-text">Account {status.account_id}</span> ·{" "}
              {status.server} · applied {status.last_applied_at ?? "—"}
            </>
          ) : (
            "ماشي مهيأ بعد — جد بيانات الحساب تحت. البيانات كتت-encrypt وكتمر Phase من Cloudflare → Gateway → MetaTrader."
          )}
        </p>
      </div>

      <div className="space-y-3">
        <label className="flex flex-col gap-1 text-[11px] uppercase tracking-wide text-tv-muted">
          Login (account number)
          <input
            value={login}
            onChange={(e) => setLogin(e.target.value)}
            placeholder="e.g. 12345678"
            spellCheck={false}
            className={inputClass()}
          />
        </label>
        <label className="flex flex-col gap-1 text-[11px] uppercase tracking-wide text-tv-muted">
          Password
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            spellCheck={false}
            className={inputClass()}
          />
        </label>
        <label className="flex flex-col gap-1 text-[11px] uppercase tracking-wide text-tv-muted">
          Server
          <input
            value={server}
            onChange={(e) => setServer(e.target.value)}
            placeholder="e.g. FTMO-Demo2"
            spellCheck={false}
            className={inputClass()}
          />
        </label>
        <label className="flex flex-col gap-1 text-[11px] uppercase tracking-wide text-tv-muted">
          MT5 Setup URL (broker installer — اختياري)
          <input
            value={setupUrl}
            onChange={(e) => setSetupUrl(e.target.value)}
            placeholder="https://download.…/mt5setup.exe"
            spellCheck={false}
            className={inputClass()}
          />
        </label>

        <button
          onClick={save}
          disabled={loading}
          className="w-full rounded bg-tv-blue px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-600 active:scale-[0.99] disabled:opacity-50"
        >
          {loading ? "… Sending" : "💾 Save Broker Credentials"}
        </button>

        {msg && <p className={`font-mono text-xs ${toneColor}`}>{msg.text}</p>}
      </div>

      <div className="rounded border border-tv-border bg-tv-panel/60 px-3 py-2 text-[11px] leading-relaxed text-tv-muted">
        <b className="text-tv-text">Security flow:</b> Cloudflare Dashboard → Worker
        <span className="mx-1 text-tv-yellow">AES-GCM</span>encrypted at rest (D1) → JWT →
        Oracle Gateway → <span className="mx-1 text-tv-yellow">0600</span>broker.json → MT5
        auto-login. Password عاد ما كترجعش أبداً للواجهة.
      </div>
    </div>
  );
}